/**
 * A country's pyramid (designs/Caged Tiers, docs/game-design.md §7.2): its divisions from Legend
 * down, with series and players, the player's own marked. A division with several series lists
 * them, a page at a time; a series opens on League.
 */
import { roman, type CountryView, type SeriesSummary } from '@caged/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, MenuButton } from '../components/Chrome';
import ui from '../components/ui.module.css';
import { countryNames } from '../countries';
import { useLayoutMode, useNow, useScrollTop } from '../hooks';
import { FightChip, Flag } from '../league';
import '../ui.css';
import s from './League.module.css';

function Divisions({ view }: { view: CountryView }) {
  const { t } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const mine = useLeague((st) => st.mine);
  const own = mine?.status === 'ok' && mine.country === view.country ? mine : null;
  const open = async (division: number, series: number) => {
    if (series > 1) {
      openLeague('country', { country: view.country, division });
      return;
    }
    // Division I is a single series: straight to its table.
    const api = await import('../../net/leagueApi');
    const page = await api.fetchDivision(view.country, division, 0).catch(() => null);
    const id = page?.series[0]?.id;
    if (id) openLeague('league', own?.series.id === id ? null : { series: id });
  };
  return (
    <>
      <span className={[ui.label, s.note].join(' ')}>{t('screens.country.divisionsTitle')}</span>
      {view.divisions.map((d, i) => {
        const mineHere = own?.series.division === d.division;
        return (
          <button
            key={d.division}
            type="button"
            className={[s.division, mineHere ? s.divisionMine : ''].join(' ')}
            style={{ animationDelay: `${0.1 + i * 0.06}s` }}
            onClick={() => void open(d.division, d.series)}
          >
            <span className={[s.badge, d.division === 1 ? s.badgeTop : ''].join(' ')} aria-hidden="true">
              <span className={s.badgeShadow} />
              <span className={s.badgeFace}>{roman(d.division)}</span>
            </span>
            <span className={s.listText}>
              <span className={s.divisionName}>
                {t(`screens.league.divisions.${d.name}`)}
                {mineHere && <span className={ui.chip}>{t('screens.league.youPos', { pos: own!.table.find((r) => r.me)?.position ?? '–' })}</span>}
              </span>
              <span className={s.meta}>
                {t('screens.country.seriesCount', { count: d.series })} · {t('screens.leagues.playersCount', { count: d.humans })}
              </span>
            </span>
            <span className={s.chev} aria-hidden="true">
              →
            </span>
          </button>
        );
      })}
    </>
  );
}

/** The country's national champions (§7.9). */
function HallOfFame({ champions }: { champions: CountryView['champions'] }) {
  const { t } = useTranslation();
  return (
    <section className={[ui.card, s.list, s.hall].join(' ')} aria-labelledby="hall-of-fame">
      <h2 id="hall-of-fame" className={[ui.label, s.hallTitle].join(' ')}>
        {t('screens.country.hallOfFame')}
      </h2>
      {champions.length ? (
        <ol className={s.hallList}>
          {champions.map((c) => (
            <li key={c.season} className={s.hallRow}>
              <span aria-hidden="true">🏆</span>
              <span className={s.listName} data-name="">
                {c.name}
              </span>
              <span className={s.meta}>{t('screens.league.season', { season: c.season })}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className={s.empty}>{t('screens.country.noChampions')}</p>
      )}
    </section>
  );
}

function SeriesList({ country, division }: { country: string; division: number }) {
  const { t } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const mine = useLeague((st) => st.mine);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{ series: SeriesSummary[]; pages: number } | null>(null);
  useEffect(() => {
    setData(null);
    void import('../../net/leagueApi')
      .then((api) => api.fetchDivision(country, division, page))
      .then(setData, () => setData({ series: [], pages: 1 }));
  }, [country, division, page]);
  const ownId = mine?.status === 'ok' ? mine.series.id : null;
  return (
    <>
      <ul className={[ui.card, s.list].join(' ')}>
        {(data?.series ?? []).map((x) => (
          <li key={x.id}>
            <button type="button" className={s.listRow} onClick={() => openLeague('league', x.id === ownId ? null : { series: x.id })}>
              <span className={s.badgeFace} style={{ position: 'static', width: 40, height: 40 }} aria-hidden="true">
                {x.label}
              </span>
              <span className={s.listText}>
                <span className={s.listName}>
                  {x.label}
                  {x.id === ownId && <> · {t('screens.league.you')}</>}
                </span>
                <span className={s.meta}>{t('screens.leagues.playersCount', { count: x.humans })}</span>
              </span>
              <span className={s.chev} aria-hidden="true">
                →
              </span>
            </button>
          </li>
        ))}
        {!data && <li className={s.empty}>{t('screens.league.loading')}</li>}
      </ul>
      {data && data.pages > 1 && (
        <div className={s.pager}>
          <button type="button" className={s.dayBtn} onClick={() => setPage((p) => p - 1)} disabled={page <= 0} aria-label={t('screens.country.prevPage')}>
            ‹
          </button>
          <span className={ui.label}>{t('screens.country.page', { page: page + 1, pages: data.pages })}</span>
          <button type="button" className={s.dayBtn} onClick={() => setPage((p) => p + 1)} disabled={page >= data.pages - 1} aria-label={t('screens.country.nextPage')}>
            ›
          </button>
        </div>
      )}
    </>
  );
}

export function CountryScreen() {
  const { t, i18n } = useTranslation();
  const layout = useLayoutMode();
  const target = useApp((st) => st.leagueTarget);
  const leagueBack = useApp((st) => st.leagueBack);
  const now = useNow(30_000);
  const country = target?.country ?? '';
  const [view, setView] = useState<CountryView | null>(null);
  const [failed, setFailed] = useState(false);
  const names = useMemo(() => countryNames(i18n.language), [i18n.language]);
  useScrollTop();
  useEffect(() => {
    setView(null);
    setFailed(false);
    void import('../../net/leagueApi')
      .then((api) => api.fetchCountry(country))
      .then(setView, () => setFailed(true));
  }, [country]);

  const division = target?.division;
  const head = (
    <div className={s.countryHead}>
      <Flag country={country} className={s.bigFlag} />
      <div className={s.listText}>
        <h1 className={s.pageTitle}>{division ? t(`screens.league.divisions.${view?.divisions[division - 1]?.name ?? 'rookie'}`) : names.name(country)}</h1>
        {view && (
          <span className={s.meta}>
            {division
              ? `${names.name(country)} · ${t('screens.country.seriesCount', { count: view.divisions[division - 1]?.series ?? 0 })}`
              : `${t('screens.leagues.divisionsCount', { count: view.divisions.length })} · ${t('screens.league.season', { season: view.season })}`}
          </span>
        )}
      </div>
    </div>
  );
  const body = (
    <div className={s.stack}>
      {!view ? (
        <p className={s.empty}>{failed ? t('screens.league.failed') : t('screens.league.loading')}</p>
      ) : division ? (
        <SeriesList country={country} division={division} />
      ) : (
        <>
          <Divisions view={view} />
          <HallOfFame champions={view.champions} />
        </>
      )}
    </div>
  );

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <div className={s.deskColumn}>
          <div className={s.deskHeroTop}>
            <button type="button" className={s.backLink} onClick={leagueBack}>
              ← {t('common.back')}
            </button>
            {view && <FightChip next={view.next} now={now} />}
          </div>
          {head}
          {body}
        </div>
      </div>
    );
  }
  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <div className={s.topBar}>
          <button type="button" className={ui.iconBtn} onClick={leagueBack} aria-label={t('common.back')}>
            <span aria-hidden="true">←</span>
          </button>
          {view && <FightChip next={view.next} now={now} compact />}
          <MenuButton />
        </div>
        {head}
        {body}
      </div>
    </div>
  );
}
